import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, DollarSign, Users, Lock, Globe2, Shield, ArrowDownCircle, Clock, Shuffle, Ban as Bandage, Calendar, RotateCcw, ListOrdered } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import { getLeagueSettings } from '../../lib/api/leagueSettings';
import { supabase, generateDraftPicksForLeague, assignGuardianTeamToUser } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { impersonateUserRpc } from '../../lib/api/impersonation';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { GenerateDraftPicksModal } from './GenerateDraftPicksModal';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';

//

export function LeagueSettings() {
  // Tracker cette page
  usePageTracking('leaguesetting');
  const { isStandardFlexible } = useLeagueMode();
  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);
  const { language } = useLanguageStore();
  const [isSalaryCap, setIsSalaryCap] = useState(true);
  const [salaryCapAmount, setSalaryCapAmount] = useState(88000000);
  const [isReserveSalaryCap, setIsReserveSalaryCap] = useState(false);
  const [reserveSalaryCapAmount, setReserveSalaryCapAmount] = useState(15000000);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCommissioner, setIsCommissioner] = useState(false);
  const dynastyOnlyDisabled = isStandardFlexible || !isCommissioner;
  const [isMainCommissioner, setIsMainCommissioner] = useState(false);
  const [isPrivateLeague, setIsPrivateLeague] = useState(false);
  const [maxDemotions, setMaxDemotions] = useState(10);
  const [maxSignings, setMaxSignings] = useState(15);
  const [countFreeAgentSignings, setCountFreeAgentSignings] = useState<boolean>(true);
  const [isNoMovementWindowEnabled, setIsNoMovementWindowEnabled] = useState(false);
  const [noMovementStartTime, setNoMovementStartTime] = useState<string>('00:00');
  const [noMovementEndTime, setNoMovementEndTime] = useState<string>('00:00');
  const [noMovementStartDay, setNoMovementStartDay] = useState<number>(1);
  const [noMovementEndDay, setNoMovementEndDay] = useState<number>(1);
  const [isDraftMode, setIsDraftMode] = useState(false);
  const [draftTimePerPick, setDraftTimePerPick] = useState(2);
  const [draftOrderType, setDraftOrderType] = useState('random');
  const [isLoadingStandingsOrder, setIsLoadingStandingsOrder] = useState(false);
  const [draftFormatType, setDraftFormatType] = useState('linear');
  const [isDraftBlocked, setIsDraftBlocked] = useState(false);
  const [leagueMembers, setLeagueMembers] = useState<{ users_user_id: number; username: string }[]>([]);
  const [manualDraftOrder, setManualDraftOrder] = useState<number[]>([]);
  const [injuredReserveLimit, setInjuredReserveLimit] = useState(5);
  const [daysToComeback, setDaysToComeback] = useState(14);
  const [protectionList, setProtectionList] = useState({
    forwards: 8,
    defensemen: 3,
    goalies: 1,
    reserves: 0 // Added reserves field
  });
  const [teamComposition, setTeamComposition] = useState({
    forwards: 12,
    defensemen: 6,
    goalies: 2,
    reserves: 5,
    scratches: 0
  });
  const [useProtectionList, setUseProtectionList] = useState(false);
  const [maxDateProtectionList, setMaxDateProtectionList] = useState<string>('');
  const [currentDraftModeInDB, setCurrentDraftModeInDB] = useState(false); // Track actual DB value
  const [secondCommissioner, setSecondCommissioner] = useState<number | null>(null);
  const [nbRounds, setNbRounds] = useState<number>(10);
  const [numberOfPicks, setNumberOfPicks] = useState<number>(7);
  const [welcomeMessage, setWelcomeMessage] = useState<string>('');
  const [hasSerpentineOrderGenerated, setHasSerpentineOrderGenerated] = useState(false);
  const [hasLinearOrderGenerated, setHasLinearOrderGenerated] = useState(false);
  // Transaction settings
  const [tradeValidation, setTradeValidation] = useState<boolean>(false);
  const [isSalaryRetentionEnabled, setIsSalaryRetentionEnabled] = useState<boolean>(false);
  const [salaryRetainMaxSpot, setSalaryRetainMaxSpot] = useState<number>(3);
  const [tradeDeadline, setTradeDeadline] = useState<string>('');
  const [promotionDeadline, setPromotionDeadline] = useState<string>('');
  const [poolStartDate, setPoolStartDate] = useState<string>('');
  
  // Draft picks generation modal states
  const [showGenerateDraftPicksModal, setShowGenerateDraftPicksModal] = useState(false);
  const [isGeneratingDraftPicks, setIsGeneratingDraftPicks] = useState(false);
  const [guardiansList, setGuardiansList] = useState<{ userId: number; username: string }[]>([]);
  const [selectedGuardianSource, setSelectedGuardianSource] = useState<number | null>(null);
  const [guardianCandidates, setGuardianCandidates] = useState<{ userId: number; username: string }[]>([]);
  const [selectedGuardianRecipient, setSelectedGuardianRecipient] = useState<number | null>(null);
  const [isAssigningGuardian, setIsAssigningGuardian] = useState(false);
  
  // Points settings
  const [pointsSettings, setPointsSettings] = useState({
    forwardGoal: 1,
    forwardAssist: 1,
    defenseGoal: 1,
    defenseAssist: 1,
    goalieGoal: 1,
    goalieAssist: 1,
    goalieWin: 2,
    goalieShutout: 3,
    goalieOtLoss: 1
  });

  const normalizeBlockDay = (value: number | null | undefined) => {
    if (typeof value !== 'number') return 1;
    if (value < 1 || value > 7) return 1;
    return value;
  };

  const dayOptions = language === 'fr'
    ? [
        { value: 1, label: 'Lundi' },
        { value: 2, label: 'Mardi' },
        { value: 3, label: 'Mercredi' },
        { value: 4, label: 'Jeudi' },
        { value: 5, label: 'Vendredi' },
        { value: 6, label: 'Samedi' },
        { value: 7, label: 'Dimanche' }
      ]
    : [
        { value: 1, label: 'Monday' },
        { value: 2, label: 'Tuesday' },
        { value: 3, label: 'Wednesday' },
        { value: 4, label: 'Thursday' },
        { value: 5, label: 'Friday' },
        { value: 6, label: 'Saturday' },
        { value: 7, label: 'Sunday' }
      ];

  // Fonctions utilitaires pour la conversion temps
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return (hours * 60) + minutes;
  };

  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Impersonation / se connecter en tant que
  const startImpersonation = useUserStore((state) => state.startImpersonation);
  const stopImpersonation = useUserStore((state) => state.stopImpersonation);
  const is_impersonating = useUserStore((state) => state.is_impersonating);
  const original_username = useUserStore((state) => state.original_username);
  const [impersonateTarget, setImpersonateTarget] = useState<number | null>(null);

  useEffect(() => {
    async function checkCommissionerStatus() {
      if (!selectedLeague || !currentUserId) return;

      try {
        const { data, error } = await supabase
          .from('leagues')
          .select('admin_user_id, admin_user_id2, is_private')
          .eq('league_id', selectedLeague.id)
          .single();

        if (error) throw error;

        // Vérifier si l'utilisateur est commissaire principal ou deuxième commissaire
        const isMainCommissioner = data.admin_user_id === Number(currentUserId);
        const isSecondCommissioner = data.admin_user_id2 === Number(currentUserId);
        const hasCommissionerRights = isMainCommissioner || isSecondCommissioner;

        setIsCommissioner(hasCommissionerRights);
        setIsMainCommissioner(isMainCommissioner);
        setIsPrivateLeague(data.is_private || false);
        setSecondCommissioner(data.admin_user_id2 || null);
        
        // Ne plus rediriger automatiquement - permettre l'accès en lecture seule
        if (!hasCommissionerRights) {
          // Afficher un message d'information au lieu d'une erreur
          toast(translate('league.settings.readonly.message', language) || 'Vous pouvez consulter les paramètres de la ligue en mode lecture seule', {
            icon: '👀',
            duration: 4000
          });
        }
      } catch (error) {
        console.error('Error checking commissioner status:', error);
        navigate('/my-office');
      }
    }

    checkCommissionerStatus();
  }, [selectedLeague, currentUserId, navigate, language]);

  const handleStartImpersonation = async () => {
    if (!selectedLeague || !impersonateTarget) return;
    try {
      // Optionnel: appeler RPC serveur si disponible
      const rpcResult = await impersonateUserRpc(selectedLeague.id, impersonateTarget);
      if (rpcResult.success && rpcResult.data?.user) {
        startImpersonation(rpcResult.data.user.username, String(rpcResult.data.user.user_id));
      } else {
        const member = leagueMembers.find(m => m.users_user_id === impersonateTarget);
        if (!member) {
          toast.error('Membre introuvable');
          return;
        }
        startImpersonation(member.username, String(member.users_user_id));
      }
  // store state will reflect impersonation
      toast.success(`Vous êtes maintenant connecté en tant que ${leagueMembers.find(m => m.users_user_id === impersonateTarget)?.username}`);
      navigate('/dashboard');
    } catch (err) {
      console.error('Erreur impersonation:', err);
      toast.error('Impossible de démarrer l\'impersonation');
    }
  };

  const handleStopImpersonation = () => {
  stopImpersonation();
    toast.success('Vous êtes revenu à votre compte');
    navigate('/my-office');
  };

  useEffect(() => {
    async function loadSettings() {
      if (!selectedLeague) return;

      try {
        const { data, error } = await getLeagueSettings(selectedLeague.id);
        if (error) throw error;

        if (data) {
          setIsSalaryCap(data.salary_cap);
          setSalaryCapAmount(data.max_salary_cap);
          setIsReserveSalaryCap(data.salary_cap_reserve ?? false);
          setReserveSalaryCapAmount(data.max_salary_cap_reserve ?? 15000000);
          setMaxDemotions(data.max_demotion ?? 10);
          setMaxSignings(data.max_signing ?? 15);
          setCountFreeAgentSignings((data as any).count_free_agent_signings !== false);
          setIsDraftMode(data.draftMode ?? false);
          setCurrentDraftModeInDB(data.draftMode ?? false); // Track actual DB value
          setDraftTimePerPick(data.draftMinPerPick ?? 2);
          setInjuredReserveLimit(data.max_injury ?? 5);
          setDaysToComeback(data.day_to_comeback ?? 14);
          setIsNoMovementWindowEnabled(!!data.nomouvement);
          setNoMovementStartTime(minutesToTime(data.nmstarth ?? 0));
          setNoMovementEndTime(minutesToTime(data.nmendh ?? 0));
          setNoMovementStartDay(normalizeBlockDay(data.nmstartd));
          setNoMovementEndDay(normalizeBlockDay(data.nmendd));
          setUseProtectionList(data.protectionlist ?? false);
          setIsDraftBlocked(!!data.block_draft);
          setWelcomeMessage(data.message_acceuil ?? '');
          
          if (data.maxdate_protectionlist) {
            // Format the date to YYYY-MM-DD for the date input
            const date = new Date(data.maxdate_protectionlist);
            const formattedDate = date.toISOString().split('T')[0];
            setMaxDateProtectionList(formattedDate);
          }
          
          if (data.isLineaire && !data.isSerpentin) {
            setDraftFormatType('linear');
          } else if (!data.isLineaire && data.isSerpentin) {
            setDraftFormatType('serpentine');
          }
          
          setTeamComposition({
            forwards: data.number_foward ?? 12,
            defensemen: data.number_def ?? 6,
            goalies: data.number_goalie ?? 2,
            reserves: data.number_reserve ?? 5,
            scratches: data.number_scratch ?? 0
          });
          setProtectionList({
            forwards: data.number_foward_protect ?? 8,
            defensemen: data.number_def_protect ?? 3,
            goalies: data.number_goalie_protect ?? 1,
            reserves: data.number_reserve_protect ?? 1 // Added reserves protection
          });
          
          // Load number of picks
          setNumberOfPicks(data.numberof_picks ?? 7);
          
          // Load points settings
          setPointsSettings({
            forwardGoal: data.pts_goal_att ?? 1,
            forwardAssist: data.pts_ass_att ?? 1,
            defenseGoal: data.pts_goal_def ?? 2,
            defenseAssist: data.pts_ass_def ?? 1,
            goalieGoal: data.pts_goal_g ?? 5,
            goalieAssist: data.pts_ass_g ?? 1,
            goalieWin: data.pts_win_g ?? 2,
            goalieShutout: data.pts_shu_g ?? 3,
            goalieOtLoss: data.pts_otlosse_g ?? 1
          });
          // Transaction settings
          setTradeValidation(!!data.tradevalidation);
          setIsSalaryRetentionEnabled(!!data.salaryretain);
          setSalaryRetainMaxSpot(data.salaryretainmaxspot ?? 3);
          
          // Trade deadline
          if (data.trade_deadline) {
            const date = new Date(data.trade_deadline);
            const formattedDate = date.toISOString().split('T')[0];
            setTradeDeadline(formattedDate);
          }

          // Promotion/demotion deadline
          if (data.deadline_demotion_promotion) {
            const date = new Date(data.deadline_demotion_promotion);
            const formattedDate = date.toISOString().split('T')[0];
            setPromotionDeadline(formattedDate);
          }

          // Pool start date
          if (data.pool_start_date) {
            setPoolStartDate(data.pool_start_date.split('T')[0]);
          } else {
            setPoolStartDate('');
          }
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        toast.error(translate('settings.error', language));
      } finally {
        setLoading(false);
      }
    }

    loadSettings();
  }, [selectedLeague, language]);

  // Reset order generated flags when draft mode is turned off or format changes
  useEffect(() => {
    if (!isDraftMode) {
      setHasSerpentineOrderGenerated(false);
      setHasLinearOrderGenerated(false);
      return;
    }
    if (draftFormatType !== 'serpentine') {
      setHasSerpentineOrderGenerated(false);
    }
    if (draftFormatType !== 'linear') {
      setHasLinearOrderGenerated(false);
    }
  }, [isDraftMode, draftFormatType]);

  const fetchLeagueMembers = useCallback(async () => {
    if (!selectedLeague) return;

    try {
      const { data: teamData, error } = await supabase
        .from('getallteam')
        .select('users_user_id, username')
        .eq('league_id', selectedLeague.id);

      if (error) throw error;

      const teamMembers = teamData ?? [];
      const teamOwnerIds = new Set(teamMembers.map(member => member.users_user_id));

      const { data: membershipData, error: membershipError } = await supabase
        .from('leagues_users')
        .select('user_id')
        .eq('league_id', selectedLeague.id);

      if (membershipError) throw membershipError;

      let additionalMembers: { users_user_id: number; username: string }[] = [];
      if (membershipData && membershipData.length > 0) {
        const missingIds = membershipData
          .map(member => member.user_id)
          .filter((userId): userId is number => userId != null && !teamOwnerIds.has(userId));

        if (missingIds.length > 0) {
          const { data: missingUsersData, error: missingUsersError } = await supabase
            .from('users')
            .select('user_id, username')
            .in('user_id', missingIds);

          if (missingUsersError) throw missingUsersError;

          if (missingUsersData) {
            additionalMembers = missingUsersData.map(user => ({
              users_user_id: user.user_id,
              username: user.username,
            }));
          }
        }
      }

      const combinedMembersMap = new Map<number, { users_user_id: number; username: string }>();
      [...teamMembers, ...additionalMembers].forEach(member => {
        if (member.users_user_id != null) {
          combinedMembersMap.set(member.users_user_id, member);
        }
      });

      const combinedMembers = Array.from(combinedMembersMap.values()).sort((a, b) =>
        a.username.localeCompare(b.username, 'fr', { sensitivity: 'base' })
      );

      setLeagueMembers(combinedMembers);

      if (manualDraftOrder.length === 0 && teamMembers.length > 0) {
        setManualDraftOrder(teamMembers.map(member => member.users_user_id));
      }

      const { data: draftOrderData, error: draftOrderError } = await supabase
        .from('draft_order')
        .select('user_id, draft_position, myturn')
        .eq('league_id', selectedLeague.id)
        .order('draft_position', { ascending: true });

      if (draftOrderError) {
        console.error('Error loading draft order:', draftOrderError);
      } else if (draftOrderData && draftOrderData.length > 0) {
        const orderedUserIds = draftOrderData
          .sort((a, b) => a.draft_position - b.draft_position)
          .map(item => item.user_id);

        if (orderedUserIds.length > 0) {
          setManualDraftOrder(orderedUserIds);
          const membersCount = teamMembers.length;
          if (draftOrderData.length > membersCount) {
            setHasSerpentineOrderGenerated(true);
          }
        }
      }

      const { data: rosterData, error: rosterError } = await supabase
        .from('league_players_user')
        .select('user_id')
        .eq('league_id', selectedLeague.id);

      if (rosterError) throw rosterError;

      const playerCounts = new Map<number, number>();
      (rosterData ?? []).forEach((row: { user_id: number | null }) => {
        if (row.user_id != null) {
          playerCounts.set(row.user_id, (playerCounts.get(row.user_id) ?? 0) + 1);
        }
      });

      const { data: pickData, error: pickError } = await supabase
        .from('draftpicks_user_league')
        .select('user_id')
        .eq('league_id', selectedLeague.id);

      if (pickError) throw pickError;

      const pickCounts = new Map<number, number>();
      (pickData ?? []).forEach((row: { user_id: number | null }) => {
        if (row.user_id != null) {
          pickCounts.set(row.user_id, (pickCounts.get(row.user_id) ?? 0) + 1);
        }
      });

      // Trouver tous les comptes DG tutelle (username contient 'dg tutelle')
      const foundGuardians = combinedMembers
        .filter(member => member.username?.toLowerCase().includes('dg tutelle'))
        .map(member => ({ userId: member.users_user_id, username: member.username }));
      setGuardiansList(foundGuardians);
      const guardianIds = new Set(foundGuardians.map(g => g.userId));

      // Réinitialiser la sélection source si le guardian sélectionné n'existe plus
      setSelectedGuardianSource(prev => {
        if (prev && !foundGuardians.some(g => g.userId === prev)) return null;
        return prev;
      });

      const candidates = combinedMembers.filter(member => {
        const userId = member.users_user_id;
        if (userId == null) return false;
        if (guardianIds.has(userId)) return false;

        const hasPlayers = (playerCounts.get(userId) ?? 0) > 0;
        const hasPicks = (pickCounts.get(userId) ?? 0) > 0;
        return !hasPlayers && !hasPicks;
      });

      setGuardianCandidates(
        candidates.map(member => ({ userId: member.users_user_id, username: member.username }))
      );

      setSelectedGuardianRecipient(prev => {
        if (prev && !candidates.some(member => member.users_user_id === prev)) {
          return null;
        }
        return prev;
      });
    } catch (error) {
      console.error('Error fetching league members:', error);
    }
  }, [selectedLeague, manualDraftOrder.length]);

  useEffect(() => {
    fetchLeagueMembers();
  }, [fetchLeagueMembers]);

  const handleSave = async () => {
    if (!selectedLeague || !isCommissioner) return;

    setSaving(true);

    // Vérification directe des champs vides en consultant les éléments DOM
    const fieldsToCheck: Array<{ id: string; name: string; optional?: boolean }> = [
      { id: 'forwards', name: 'nombre d\'attaquants' },
      { id: 'defensemen', name: 'nombre de défenseurs' },
      { id: 'goalies', name: 'nombre de gardiens' },
      { id: 'reserves', name: 'nombre de joueurs de réserve' },
      { id: 'scratches', name: 'nombre de scratch players' },
      { id: 'maxDemotions', name: 'nombre maximum de rétrogradations' },
      { id: 'maxSignings', name: 'nombre maximum de signatures' },
      { id: 'injuredReserve', name: 'limite de la liste des blessés' },
      { id: 'daysToComeback', name: 'délai de retour des blessés (en jours)' },
      { id: 'forwardGoal', name: 'points pour but d\'attaquant' },
      { id: 'forwardAssist', name: 'points pour passe d\'attaquant' },
      { id: 'defenseGoal', name: 'points pour but de défenseur' },
      { id: 'defenseAssist', name: 'points pour passe de défenseur' },
      { id: 'goalieGoal', name: 'points pour but de gardien' },
      { id: 'goalieAssist', name: 'points pour passe de gardien' },
      { id: 'goalieWin', name: 'points pour victoire de gardien' },
      { id: 'goalieShutout', name: 'points pour blanchissage' },
      { id: 'goalieOtLoss', name: 'points pour défaite en prolongation de gardien' },
      { id: 'numberOfPicks', name: 'nombre de choix au repêchage' },
      { id: 'welcomeMessage', name: "message d'accueil", optional: true }
    ];

    // Ajouter les champs conditionnels
    if (isDraftMode) {
      fieldsToCheck.push({ id: 'draftTimePerPick', name: 'temps par sélection de draft' });
    }

    if (isSalaryCap) {
      fieldsToCheck.push({ id: 'salaryCapAmount', name: 'montant du plafond salarial principal' });
    }

    if (isReserveSalaryCap) {
      fieldsToCheck.push({ id: 'reserveSalaryCapAmount', name: 'montant du plafond salarial de réserve' });
    }

    if (useProtectionList) {
      fieldsToCheck.push(
        { id: 'forwardsProtect', name: 'nombre d\'attaquants à protéger' },
        { id: 'defensemenProtect', name: 'nombre de défenseurs à protéger' },
        { id: 'goaliesProtect', name: 'nombre de gardiens à protéger' },
        { id: 'reservesProtect', name: 'nombre de joueurs de réserve à protéger' }
      );
    }

    if (isNoMovementWindowEnabled) {
      fieldsToCheck.push(
        { id: 'noMovementStartTime', name: 'heure de début du blocage' },
        { id: 'noMovementEndTime', name: 'heure de fin du blocage' },
        { id: 'noMovementStartDay', name: 'jour de début du blocage' },
        { id: 'noMovementEndDay', name: 'jour de fin du blocage' }
      );
    }

    // Vérifier les champs vides
    const emptyFields = fieldsToCheck.filter(field => {
      const element = document.getElementById(field.id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (!element) return false;
      if (field.optional) return false;
      return element.value === '' || element.value.trim() === '';
    });

    // Vérifier les champs qui doivent être positifs
    const positiveFields = [];
    if (isDraftMode) {
      const draftTimeElement = document.getElementById('draftTimePerPick') as HTMLInputElement;
      if (draftTimeElement && (draftTimeElement.value === '' || parseFloat(draftTimeElement.value) <= 0)) {
        positiveFields.push('temps par sélection de draft');
      }
    }

    if (isSalaryCap) {
      const salaryCapElement = document.getElementById('salaryCapAmount') as HTMLInputElement;
      if (salaryCapElement && (salaryCapElement.value === '' || parseFloat(salaryCapElement.value) <= 0)) {
        positiveFields.push('montant du plafond salarial principal');
      }
    }

    if (isReserveSalaryCap) {
      const reserveSalaryCapElement = document.getElementById('reserveSalaryCapAmount') as HTMLInputElement;
      if (reserveSalaryCapElement && (reserveSalaryCapElement.value === '' || parseFloat(reserveSalaryCapElement.value) <= 0)) {
        positiveFields.push('montant du plafond salarial de réserve');
      }
    }

    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.map(field => field.name).join(', ');
      toast.error(`Vous ne pouvez pas laisser un champ vide. Veuillez entrer une valeur pour: ${fieldNames}. (Vous pouvez entrer 0 au besoin)`);
      setSaving(false);
      return;
    }

    if (positiveFields.length > 0) {
      const fieldNames = positiveFields.join(', ');
      toast.error(`Ces champs doivent avoir une valeur supérieure à 0: ${fieldNames}`);
      setSaving(false);
      return;
    }

    if (isNoMovementWindowEnabled) {
      const startDay = Number(noMovementStartDay);
      const endDay = Number(noMovementEndDay);
      const isValidDay = (value: number) => Number.isInteger(value) && value >= 1 && value <= 7;
      
      // Validation des heures au format HH:MM
      const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(noMovementStartTime) || !timeRegex.test(noMovementEndTime)) {
        toast.error(
          language === 'fr'
            ? 'Les heures doivent être au format HH:MM (par exemple: 06:30, 18:00).'
            : 'Hours must be in HH:MM format (example: 06:30, 18:00).'
        );
        setSaving(false);
        return;
      }

      if (!isValidDay(startDay) || !isValidDay(endDay)) {
        toast.error(
          language === 'fr'
            ? 'Les jours de blocage doivent être des valeurs entières entre 1 (lundi) et 7 (dimanche).'
            : 'Blocking days must be whole numbers between 1 (Monday) and 7 (Sunday).'
        );
        setSaving(false);
        return;
      }
    }

    // Validate protection list date if protection list is enabled
    if (useProtectionList && !maxDateProtectionList) {
      toast.error('Veuillez entrer une date limite pour la liste de protection');
      setSaving(false);
      return;
    }

    // Validation pour le mode draft et l'ordre complet à générer selon le format
    if (isDraftMode && !currentDraftModeInDB) {
      if (draftFormatType === 'serpentine' && !hasSerpentineOrderGenerated) {
        toast.error('Vous devez générer l\'ordre serpentin avant de lancer le draft');
        setSaving(false);
        return;
      }
      if (draftFormatType === 'linear' && !hasLinearOrderGenerated) {
        toast.error('Vous devez générer l\'ordre linéaire avant de lancer le draft');
        setSaving(false);
        return;
      }
    }

    setSaving(true);
    try {
      const { error: privacyError } = await supabase
        .from('leagues')
        .update({ 
          is_private: isPrivateLeague,
          admin_user_id2: secondCommissioner
        })
        .eq('league_id', selectedLeague.id);

      if (privacyError) throw privacyError;

      const isLineaire = draftFormatType === 'linear';
      const isSerpentin = draftFormatType === 'serpentine';

      // Convert tradeDeadline to a proper timestamp if set
      let tradeDeadlineTimestamp = null;
      if (tradeDeadline) {
        tradeDeadlineTimestamp = new Date(tradeDeadline);
        if (isNaN(tradeDeadlineTimestamp.getTime())) {
          tradeDeadlineTimestamp = null;
        }
      }

      // Convert promotionDeadline to a proper timestamp if set
      let promotionDeadlineTimestamp = null;
      if (promotionDeadline) {
        promotionDeadlineTimestamp = new Date(promotionDeadline);
        if (isNaN(promotionDeadlineTimestamp.getTime())) {
          promotionDeadlineTimestamp = null;
        }
      }

      if (!isDraftMode) {
        const { error: draftedPlayersError } = await supabase
          .from('drafted_players')
          .update({ is_past_draft: true })
          .eq('league_id', selectedLeague.id);

        if (draftedPlayersError) {
          console.error('Error updating drafted players:', draftedPlayersError);
        }
      }

      // Convert maxDateProtectionList to a proper timestamp if protection list is enabled
      let protectionListDate = null;
      if (useProtectionList && maxDateProtectionList) {
        protectionListDate = new Date(maxDateProtectionList);
        if (isNaN(protectionListDate.getTime())) {
          protectionListDate = null;
        }
      }

      // Add the new settings to the saveLeagueSettings call
      const { error } = await supabase
        .from('league_settings')
        .update({
          salary_cap: isSalaryCap,
          max_salary_cap: salaryCapAmount,
          number_foward: teamComposition.forwards,
          number_def: teamComposition.defensemen,
          number_goalie: teamComposition.goalies,
          number_reserve: teamComposition.reserves,
          number_scratch: teamComposition.scratches,
          salary_cap_reserve: isReserveSalaryCap,
          max_salary_cap_reserve: reserveSalaryCapAmount,
          number_foward_protect: protectionList.forwards,
          number_def_protect: protectionList.defensemen,
          number_goalie_protect: protectionList.goalies,
          max_demotion: maxDemotions,
          max_signing: maxSignings,
          count_free_agent_signings: countFreeAgentSignings,
          draftMode: isDraftMode,
          draftMinPerPick: draftTimePerPick,
          isLineaire: isLineaire,
          isSerpentin: isSerpentin,
          current_pick: isDraftMode ? undefined : 0, // Reset current_pick to 0 when draft mode is disabled
          max_injury: injuredReserveLimit,
          day_to_comeback: daysToComeback,
          protectionlist: useProtectionList,
          maxdate_protectionlist: protectionListDate,
          number_reserve_protect: protectionList.reserves,
          block_draft: isDraftBlocked,
          message_acceuil: welcomeMessage,
          numberof_picks: numberOfPicks,
          nomouvement: isNoMovementWindowEnabled,
          nmstarth: isNoMovementWindowEnabled ? timeToMinutes(noMovementStartTime) : null,
          nmendh: isNoMovementWindowEnabled ? timeToMinutes(noMovementEndTime) : null,
          nmstartd: isNoMovementWindowEnabled ? noMovementStartDay : null,
          nmendd: isNoMovementWindowEnabled ? noMovementEndDay : null,
          // Points settings
          pts_goal_att: pointsSettings.forwardGoal,
          pts_ass_att: pointsSettings.forwardAssist,
          pts_goal_def: pointsSettings.defenseGoal,
          pts_ass_def: pointsSettings.defenseAssist,
          pts_goal_g: pointsSettings.goalieGoal,
          pts_ass_g: pointsSettings.goalieAssist,
          pts_win_g: pointsSettings.goalieWin,
          pts_shu_g: pointsSettings.goalieShutout,
          pts_otlosse_g: pointsSettings.goalieOtLoss
          ,
          // Transaction setting
          conteroffer: false,
          tradevalidation: tradeValidation,
          salaryretain: isSalaryRetentionEnabled,
          salaryretainmaxspot: salaryRetainMaxSpot,
          trade_deadline: tradeDeadlineTimestamp,
          deadline_demotion_promotion: promotionDeadlineTimestamp,
          pool_start_date: poolStartDate || null
        })
        .eq('league_id', selectedLeague.id);

      if (error) throw error;

      // If a pool start date is set, capture a baseline snapshot of the current standings
      // so the delta calculation (current - baseline) starts from zero for that date.
      if (poolStartDate) {
        await supabase.rpc('capture_pool_start_snapshot', { p_league_id: selectedLeague.id });
      }

      // Important: do not touch draft_order while a draft is already in progress.
      // Only (re)generate draft order when starting a new draft (currentDraftModeInDB === false).
      // For serpentine format, the dedicated RPC button generates the full order; don't overwrite here.
      if (isDraftMode && !currentDraftModeInDB) {
        const isLinearFormat = draftFormatType === 'linear';
        if (isLinearFormat && manualDraftOrder.length > 0) {
          await supabase
            .from('draft_order')
            .delete()
            .eq('league_id', selectedLeague.id);

          // Déduplication de sécurité (préserve l'ordre d'apparition) avant insertion en DB
          const dedupedManualDraftOrder = Array.from(new Set(manualDraftOrder));
          if (dedupedManualDraftOrder.length !== manualDraftOrder.length) {
            console.warn('Doublons supprimés dans manualDraftOrder avant insertion dans draft_order');
          }

          const draftOrderData = dedupedManualDraftOrder.map((userId, index) => ({
            league_id: selectedLeague.id,
            user_id: userId,
            draft_position: index + 1,
            myturn: index === 0,
            time_of_pick: null
          }));

          const { error: draftOrderError } = await supabase
            .from('draft_order')
            .insert(draftOrderData);

          if (draftOrderError) {
            console.error('Error saving draft order:', draftOrderError);
            throw new Error('Failed to save draft order');
          }
        }
      }

      toast.success(translate('settings.saved', language));
      navigate('/my-office');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(translate('settings.error', language));
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, userId: number) => {
    e.dataTransfer.setData('userId', userId.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetUserId: number) => {
    e.preventDefault();
    const draggedUserId = Number(e.dataTransfer.getData('userId'));
    
    if (draggedUserId === targetUserId) return;
    
    const newOrder = [...manualDraftOrder];
    const draggedIndex = newOrder.findIndex(id => id === draggedUserId);
    const targetIndex = newOrder.findIndex(id => id === targetUserId);
    
    if (draggedIndex !== -1 && targetIndex !== -1) {
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedUserId);
      setManualDraftOrder(newOrder);
    }
  };

  const generateRandomDraftOrder = () => {
    const shuffled = [...leagueMembers.map(member => member.users_user_id)];
    
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    setManualDraftOrder(shuffled);
    toast.success('Ordre de repêchage aléatoire généré');
  };

  // Génère l'ordre de repêchage à partir du classement actuel (table standing).
  // Ordre voulu: l'équipe avec le moins de points est 1re, celle avec le plus de points est dernière.
  // Opération bloquante: en cas d'incohérence (équipe sans classement), on affiche une erreur
  // et on n'altère PAS manualDraftOrder.
  // Note: on lit directement la table `standing` (filtrée par league_id, indexée, déjà utilisée
  // par la page Classement) plutôt que la vue `getstandings` qui agrège toutes les ligues avant
  // de filtrer et provoquait un timeout (statement timeout 57014) sur les grandes ligues.
  const generateStandingsDraftOrder = async () => {
    if (!selectedLeague) return;

    setIsLoadingStandingsOrder(true);
    try {
      const { data: standingsData, error: standingsError } = await supabase
        .from('standing')
        .select('user_id, totgoals, totassists, totgoals_goalie, totassists_goalie, totwins_goalie, totshutout_goalie, tototlosses_goalie')
        .eq('league_id', selectedLeague.id);

      if (standingsError) throw standingsError;

      if (!standingsData || standingsData.length === 0) {
        toast.error("Impossible de générer l'ordre: aucune donnée de classement disponible pour cette ligue.");
        return;
      }

      const standingsWithPoints = standingsData.map(row => ({
        user_id: row.user_id,
        points:
          (row.totgoals ?? 0) +
          (row.totassists ?? 0) +
          (row.totgoals_goalie ?? 0) +
          (row.totassists_goalie ?? 0) +
          (row.totwins_goalie ?? 0) +
          (row.totshutout_goalie ?? 0) +
          (row.tototlosses_goalie ?? 0)
      }));

      // Ordre croissant: moins de points en premier, plus de points en dernier
      const sortedStandings = [...standingsWithPoints].sort((a, b) => a.points - b.points);
      const orderedUserIds = sortedStandings.map(row => row.user_id);

      // S'assurer que toutes les équipes de la ligue ont un classement (sinon ordre incomplet)
      const standingsUserIdSet = new Set(orderedUserIds);
      const missingTeams = leagueMembers.filter(member => !standingsUserIdSet.has(member.users_user_id));
      if (missingTeams.length > 0) {
        const missingNames = missingTeams.map(m => m.username).join(', ');
        toast.error(`Impossible de générer l'ordre: certaines équipes n'ont pas encore de statistiques au classement (${missingNames}).`);
        return;
      }

      // Déduplication de sécurité (préserve l'ordre d'apparition)
      const uniqueOrderedUserIds = Array.from(new Set(orderedUserIds));
      if (uniqueOrderedUserIds.length !== orderedUserIds.length) {
        console.warn('Doublons supprimés dans l\'ordre généré depuis standing');
      }

      setManualDraftOrder(uniqueOrderedUserIds);
      toast.success('Ordre de repêchage généré selon le classement actuel (moins de points en premier)');
    } catch (error) {
      console.error("Erreur lors de la génération de l'ordre selon le classement:", error);
      toast.error("Erreur lors du chargement du classement pour générer l'ordre de repêchage.");
    } finally {
      setIsLoadingStandingsOrder(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleGenerateSerpentine = async () => {
    if (!selectedLeague) return;
    try {
  // Ensure a base draft_order exists based on current selection
  await ensureBaseDraftOrder();
      // Always clear any previously generated picks for this league
      await supabase
        .from('draft_picks')
        .delete()
        .eq('league_id', selectedLeague.id);
      const { error } = await supabase.rpc('generate_serpentine_draft_order', {
        p_league_id: selectedLeague.id,
        p_rounds: nbRounds
      });
      if (error && (error as any).code !== 'PGRST116') throw error;
      setHasSerpentineOrderGenerated(true);
      toast.success('Ordre complet généré en serpentin');
    } catch (e) {
      console.error('Erreur lors de la génération de l’ordre serpentin:', e);
      toast.error('Erreur lors de la génération de l’ordre');
    }
  };

  const handleGenerateLinear = async () => {
    if (!selectedLeague) return;
    try {
      // Ensure a base draft_order exists based on current selection
      await ensureBaseDraftOrder();
      // Always clear any previously generated picks for this league
      await supabase
        .from('draft_picks')
        .delete()
        .eq('league_id', selectedLeague.id);
      const { error } = await supabase.rpc('generate_linear_draft_order', {
        p_league_id: selectedLeague.id,
        p_rounds: nbRounds
      });
      if (error && (error as any).code !== 'PGRST116') throw error;
      setHasLinearOrderGenerated(true);
      toast.success('Ordre complet généré en linéaire');
    } catch (e) {
      console.error('Erreur lors de la génération de l’ordre linéaire:', e);
      toast.error('Erreur lors de la génération de l’ordre');
    }
  };

  // Ensure base draft_order exists using the current displayed order
  const ensureBaseDraftOrder = async () => {
    if (!selectedLeague) return;
    try {
      // Determine order: prefer manualDraftOrder if set, else fallback to leagueMembers order
      const baseOrder = manualDraftOrder.length > 0
        ? manualDraftOrder
        : leagueMembers.map(m => m.users_user_id);

      if (!baseOrder || baseOrder.length === 0) {
        throw new Error('Aucun membre de ligue pour créer un ordre de repêchage');
      }

      // Déduplication de sécurité (préserve l'ordre d'apparition) avant insertion en DB
      const dedupedBaseOrder = Array.from(new Set(baseOrder));
      if (dedupedBaseOrder.length !== baseOrder.length) {
        console.warn('Doublons supprimés dans baseOrder avant insertion dans draft_order');
      }

      // Clean slate then insert base order
      await supabase
        .from('draft_order')
        .delete()
        .eq('league_id', selectedLeague.id);

      const rows = dedupedBaseOrder.map((userId, index) => ({
        league_id: selectedLeague.id,
        user_id: userId,
        draft_position: index + 1,
        myturn: index === 0,
        time_of_pick: null
      }));

      const { error: insertError } = await supabase
        .from('draft_order')
        .insert(rows);
      if (insertError) throw insertError;
    } catch (err) {
      console.error('ensureBaseDraftOrder failed:', err);
      throw err;
    }
  };

  // Fonction pour gérer la génération des choix de repêchage
  const handleGenerateDraftPicks = async () => {
    if (!selectedLeague || !isCommissioner) return;

    setIsGeneratingDraftPicks(true);
    try {
      const result = await generateDraftPicksForLeague(selectedLeague.id, numberOfPicks);
      
      if (result.success) {
        toast.success(`Choix de repêchage générés avec succès ! ${result.data.inserted_count} choix créés pour ${result.data.rounds_generated} rondes.`);
        setShowGenerateDraftPicksModal(false);
      } else {
        toast.error(result.error?.message || 'Erreur lors de la génération des choix');
      }
    } catch (error: any) {
      console.error('Erreur lors de la génération des choix:', error);
      toast.error('Erreur lors de la génération des choix');
    } finally {
      setIsGeneratingDraftPicks(false);
    }
  };

  const handleAssignGuardianTeam = async () => {
    if (!selectedLeague || !selectedGuardianRecipient) return;

    // Déterminer la source: si un seul guardian, l'utiliser automatiquement
    const sourceId = guardiansList.length === 1
      ? guardiansList[0].userId
      : selectedGuardianSource;

    if (!sourceId) {
      toast.error('Veuillez sélectionner le DG tutelle source à transférer.');
      return;
    }

    setIsAssigningGuardian(true);
    try {
      const result = await assignGuardianTeamToUser(selectedLeague.id, selectedGuardianRecipient, sourceId);

      if (result.success) {
        toast.success(
          translate('league.guardian.assignment.success', language) ||
            "L'équipe tutelle a été assignée avec succès."
        );
        setSelectedGuardianRecipient(null);
        await fetchLeagueMembers();
      } else {
        const message =
          result.error?.message ||
          translate('league.guardian.assignment.error', language) ||
          "Impossible d'assigner l'équipe tutelle.";
        toast.error(message);
      }
    } catch (error) {
      console.error('Error assigning guardian team:', error);
      toast.error(
        translate('league.guardian.assignment.error.generic', language) ||
          "Erreur lors de l'assignation de l'équipe tutelle."
      );
    } finally {
      setIsAssigningGuardian(false);
    }
  };

  const handleResetSalaryRetention = () => {
    if (!isCommissioner) return;
    toast(
      translate('salary.retain.reset.placeholder', language) ||
        'La réinitialisation des retenues de salaire sera disponible prochainement.',
      {
        icon: 'ℹ️'
      }
    );
  };

  const handleResetMovementsAndSignings = async () => {
    if (!selectedLeague) {
      toast.error('Aucune ligue sélectionnée.');
      return;
    }

    const confirmReset = window.confirm(
      'Êtes-vous sûr de vouloir réinitialiser les mouvements et signatures pour tous les utilisateurs de cette ligue ?'
    );

    if (!confirmReset) return;

    try {
      const { error } = await supabase
        .from('leagues_users')
        .update({
          number_of_demotion_used: 0,
          number_of_sigining_used: 0,
        })
        .eq('league_id', selectedLeague.id);

      if (error) {
        throw error;
      }

      toast.success('Mouvements et signatures réinitialisés avec succès.');
    } catch (error) {
      console.error('Erreur lors de la réinitialisation :', error);
      toast.error('Une erreur est survenue lors de la réinitialisation.');
    }
  };

  if (!selectedLeague) {
    navigate('/dashboard');
    return null;
  }

  if (loading) {
    return (
      <div className="card shadow-lg border-0">
        <div className="card-body p-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={() => navigate('/my-office')} // Ensure navigation to '/my-office'
        >
          <ArrowLeft size={20} className="me-2" />
          {translate('back', language)}
        </button>

        <div className="text-center mb-4">
          <Settings className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-4">{translate('league.settings.title', language)}</h2>
          <p className="text-muted">{selectedLeague?.name}</p>
          
          {!isCommissioner && (
            <div className="alert alert-info d-flex align-items-center mt-3" role="alert">
              <Globe2 size={20} className="me-2" />
              <div>
                <strong>Mode lecture seule</strong> - Vous pouvez consulter les paramètres de la ligue mais ne pouvez pas les modifier. Contactez un commissaire pour effectuer des changements.
              </div>
            </div>
          )}

          {/* Impersonation banner and card - visible to commissioners only */}
          {isCommissioner && (
            <>
              {is_impersonating && (
                <div className="alert alert-warning d-flex justify-content-between align-items-center">
                  <div>
                    <strong>⚠️ Impersonation active:</strong> Vous êtes connecté en tant que <em>{original_username || '...'}</em>.
                  </div>
                  <div>
                    <button className="btn btn-sm btn-outline-dark" onClick={handleStopImpersonation}>
                      Revenir à mon compte
                    </button>
                  </div>
                </div>
              )}

              <div className="card mb-4">
                <div className="card-header bg-primary text-white d-flex align-items-center">
                  <Users size={20} className="me-2" />
                  <h5 className="mb-0">Se connecter en tant que (commissaire seulement)</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label htmlFor="impersonateSelect" className="form-label">Sélectionner un DG</label>
                    <select
                      id="impersonateSelect"
                      className="form-select"
                      value={impersonateTarget ?? ''}
                      onChange={(e) => setImpersonateTarget(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">-- Choisir un membre --</option>
                      {leagueMembers.map(m => (
                        <option key={m.users_user_id} value={m.users_user_id}>
                          {m.username}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted d-block mt-2">
                      En sélectionnant un DG et en cliquant sur "Se connecter", vous serez connecté comme cet utilisateur.
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-warning" disabled={!impersonateTarget} onClick={handleStartImpersonation}>
                      Se connecter en tant que
                    </button>
                    {is_impersonating && (
                      <button className="btn btn-outline-secondary" onClick={handleStopImpersonation}>
                        Revenir à mon compte
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={`mb-5 ${!isCommissioner ? 'readonly-mode' : ''}`} style={!isCommissioner ? { pointerEvents: 'none', opacity: 0.7 } : {}}>
          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex align-items-center">
              <Lock size={20} className="me-2" />
              <h5 className="mb-0">{translate('league.privacy.settings', language)}</h5>
            </div>
            <div className="card-body">
              <div className="form-check form-switch d-flex align-items-center">
                <input
                  className="form-check-input me-3"
                  type="checkbox"
                  id="privateLeague"
                  checked={isPrivateLeague}
                  disabled={!isCommissioner}
                  onChange={() => setIsPrivateLeague(!isPrivateLeague)}
                />
                <div>
                  <label className="form-check-label d-flex align-items-center" htmlFor="privateLeague">
                    {isPrivateLeague ? (
                      <Lock size={16} className="me-2 text-warning" />
                    ) : (
                      <Globe2 size={16} className="me-2 text-success" />
                    )}
                    {translate(isPrivateLeague ? 'private.league' : 'public.league', language)}
                  </label>
                  <small className="text-muted d-block mt-1">
                    {translate(isPrivateLeague ? 'private.league.description' : 'public.league.description', language)}
                  </small>
                </div>
              </div>
            </div>
          </div>

          {isMainCommissioner && (
            <div className="card mb-4">
              <div className="card-header bg-primary text-white d-flex align-items-center">
                <Users size={20} className="me-2" />
                <h5 className="mb-0">{translate('commissioner.management', language)}</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="secondCommissioner" className="form-label">
                    {translate('second.commissioner', language)}
                  </label>
                  <select
                    className="form-select"
                    id="secondCommissioner"
                    value={secondCommissioner || ''}
                    disabled={!isCommissioner}
                    onChange={(e) => setSecondCommissioner(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">{translate('no.second.commissioner', language)}</option>
                    {leagueMembers.map((member) => (
                      <option key={member.users_user_id} value={member.users_user_id}>
                        {member.username}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted d-block mt-1">
                    {translate('choose.from.league.members', language)}
                  </small>
                </div>
                <div className="alert alert-info mt-3 mb-0">
                  <small>
                    <strong>ℹ️ {translate('second.commissioner.rights', language)}</strong>
                  </small>
                </div>
              </div>
            </div>
          )}

          {isCommissioner && (
            <div className="card mb-4">
              <div className="card-header bg-primary text-white d-flex align-items-center">
                <Shield size={20} className="me-2" />
                <h5 className="mb-0">{translate('league.guardian.assignment.title', language)}</h5>
              </div>
              <div className="card-body">
                {guardiansList.length > 0 ? (
                  <>
                    <p className="text-muted mb-3">
                      {translate('league.guardian.assignment.description', language) ||
                        "Sélectionnez un DG pour lui attribuer l'équipe tutelle."}
                    </p>

                    {/* Sélection de la source si plusieurs DG tutelle coexistent */}
                    {guardiansList.length > 1 && (
                      <div className="mb-3">
                        <label htmlFor="guardianSourceSelect" className="form-label fw-semibold">
                          DG tutelle source (équipe à transférer)
                        </label>
                        <select
                          id="guardianSourceSelect"
                          className="form-select"
                          value={selectedGuardianSource ?? ''}
                          onChange={(e) => setSelectedGuardianSource(e.target.value ? Number(e.target.value) : null)}
                          disabled={isAssigningGuardian}
                        >
                          <option value="">-- Choisir le DG tutelle source --</option>
                          {guardiansList.map((g) => (
                            <option key={g.userId} value={g.userId}>{g.username}</option>
                          ))}
                        </select>
                        <small className="text-muted d-block mt-1">
                          Plusieurs DG tutelle existent dans cette ligue. Sélectionnez lequel transférer au nouveau DG.
                        </small>
                      </div>
                    )}

                    {guardiansList.length === 1 && (
                      <p className="fw-semibold mb-3">
                        {translate('league.guardian.assignment.currentTeam', language) || 'Équipe tutelle actuelle'} :{' '}
                        <span className="text-primary">{guardiansList[0].username}</span>
                      </p>
                    )}

                    {guardianCandidates.length > 0 ? (
                      <>
                        <div className="mb-3">
                          <label htmlFor="guardianAssignmentSelect" className="form-label">
                            {translate('league.guardian.assignment.select', language)}
                          </label>
                          <select
                            id="guardianAssignmentSelect"
                            className="form-select"
                            value={selectedGuardianRecipient ?? ''}
                            onChange={(e) =>
                              setSelectedGuardianRecipient(e.target.value ? Number(e.target.value) : null)
                            }
                            disabled={isAssigningGuardian}
                          >
                            <option value="">
                              {translate('league.guardian.assignment.placeholder', language)}
                            </option>
                            {guardianCandidates.map((candidate) => (
                              <option key={candidate.userId} value={candidate.userId}>
                                {candidate.username}
                              </option>
                            ))}
                          </select>
                          <small className="text-muted d-block mt-1">
                            {translate('league.guardian.assignment.helper', language)}
                          </small>
                        </div>
                        <button
                          className="btn btn-success"
                          disabled={
                            !selectedGuardianRecipient ||
                            isAssigningGuardian ||
                            (guardiansList.length > 1 && !selectedGuardianSource)
                          }
                          onClick={handleAssignGuardianTeam}
                        >
                          {isAssigningGuardian
                            ? translate('league.guardian.assignment.loading', language) || 'Assignation...'
                            : translate('league.guardian.assignment.cta', language)}
                        </button>
                      </>
                    ) : (
                      <div className="alert alert-info mb-0">
                        {translate('league.guardian.assignment.noCandidates', language)}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="alert alert-info mb-0">
                    {translate('league.guardian.assignment.noGuardian', language)}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex align-items-center">
              <Globe2 size={20} className="me-2" />
              <h5 className="mb-0">Message d'accueil</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label htmlFor="welcomeMessage" className="form-label">
                  Message d'accueil de la ligue
                </label>
                <textarea
                  className="form-control"
                  id="welcomeMessage"
                  rows={4}
                  placeholder="Entrez un message d'accueil pour votre ligue..."
                  value={welcomeMessage}
                  disabled={!isCommissioner}
                  onChange={(e) => setWelcomeMessage(e.target.value)}
                />
                <small className="text-muted d-block mt-1">
                  Ce message sera affiché aux membres de la ligue. Vous pouvez inclure des informations importantes, des règles spéciales ou des annonces.
                </small>
              </div>
            </div>
          </div>

          {/* Transaction parameter */}
          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex align-items-center">
              <DollarSign size={20} className="me-2" />
              <h5 className="mb-0">Paramètre transaction</h5>
            </div>
            <div className="card-body">
              {isStandardFlexible && (
                <div className="alert alert-secondary d-flex align-items-center gap-2 mb-3 py-2">
                  <span>⚙️</span>
                  <span className="small">Ces paramètres sont disponibles uniquement en mode Dynasty/Keeper. Les échanges et la rétention de salaire ne s'appliquent pas au mode Standard Flexible.</span>
                </div>
              )}
              <div className="form-check form-switch mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="tradeValidation"
                  checked={tradeValidation}
                  disabled={dynastyOnlyDisabled}
                  onChange={(e) => setTradeValidation(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="tradeValidation">
                  Validation avant transaction
                </label>
                <small className="text-muted d-block mt-1">
                  Lorsque activé, les transactions nécessiteront de répondre aux exigences de la ligue pour être accepter.
                </small>
              </div>

              <hr className="my-4" />
              <div>
                <h6 className="mb-2">{translate('salary.retain.settings', language) || 'Retenue de salaire'}</h6>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="salaryRetention"
                    checked={isSalaryRetentionEnabled}
                    disabled={dynastyOnlyDisabled}
                    onChange={() => setIsSalaryRetentionEnabled(!isSalaryRetentionEnabled)}
                  />
                  <label className="form-check-label" htmlFor="salaryRetention">
                    {translate('salary.retain.toggle', language) || 'Activer la retenue de salaire pour la saison en cours'}
                  </label>
                  <small className="text-muted d-block mt-1">
                    {translate('salary.retain.description', language) || "Permet aux équipes d'inclure une portion du salaire dans les transactions pour la saison en cours uniquement."}
                  </small>
                </div>

                {isSalaryRetentionEnabled && (
                  <>
                    <div className="mb-3">
                      <label htmlFor="salaryRetainMaxSpot" className="form-label">
                        {translate('salary.retain.max.spots', language) || 'Nombre maximum de retenues de salaire par saison'}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="salaryRetainMaxSpot"
                        value={salaryRetainMaxSpot}
                        onChange={(e) => setSalaryRetainMaxSpot(parseInt(e.target.value) || 0)}
                        min="0"
                        disabled={dynastyOnlyDisabled}
                      />
                      <small className="text-muted d-block mt-1">
                        {translate('salary.retain.max.spots.description', language) || 'Nombre total de fois qu\'une équipe peut retenir du salaire durant la saison (pas simultanément).'}
                      </small>
                    </div>
                    <div className="d-flex flex-column flex-md-row align-items-md-center gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-danger d-flex align-items-center"
                        onClick={handleResetSalaryRetention}
                        disabled={dynastyOnlyDisabled}
                      >
                        <RotateCcw size={16} className="me-2" />
                        {translate('salary.retain.reset', language) || 'Réinitialiser les retenues de salaire'}
                      </button>
                      <small className="text-muted">
                        {translate('salary.retain.reset.helper', language) || 'Utilisez ce bouton pour remettre à zéro toutes les retenues de salaire actives.'}
                      </small>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex align-items-center">
              <Clock size={20} className="me-2" />
              <h5 className="mb-0">Paramètres de la saison</h5>
            </div>
            <div className="card-body">
              {isStandardFlexible && (
                <div className="alert alert-secondary d-flex align-items-center gap-2 mb-3 py-2">
                  <span>⚙️</span>
                  <span className="small">Le mode repêchage est disponible uniquement en mode Dynasty/Keeper. En mode Standard Flexible, utilisez « Bâtir mon équipe » pour constituer votre équipe.</span>
                </div>
              )}
              <div className="form-check form-switch mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="draftMode"
                  checked={isDraftMode}
                  disabled={dynastyOnlyDisabled}
                  onChange={() => {
                    const next = !isDraftMode;
                    setIsDraftMode(next);
                    // Visually default to not blocked when starting the draft
                    if (next) setIsDraftBlocked(false);
                  }}
                />
                <label className="form-check-label" htmlFor="draftMode">
                  {isDraftMode ? 'Terminer le repêchage' : 'Lancer le repechage'}
                </label>
              </div>

              {isDraftMode && (
                <div className="form-check form-switch mb-3 ms-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="blockDraft"
                    checked={isDraftBlocked}
                    disabled={dynastyOnlyDisabled}
                    onChange={() => setIsDraftBlocked(!isDraftBlocked)}
                  />
                  <label className="form-check-label" htmlFor="blockDraft">
                    Bloquer le repêchage (empêche les équipes de faire une sélection)
                  </label>
                  <small className="text-muted d-block mt-1">
                    Utilisez ceci pour mettre le repêchage en pause temporairement.
                  </small>
                </div>
              )}

              {isDraftMode && (
                <div className="ms-4">
                  {currentDraftModeInDB && (
                    <div className="alert alert-warning mb-3">
                      <strong>⚠️ Draft en cours:</strong> Les paramètres du repêchage sont verrouillés et ne peuvent pas être modifiés tant que le draft est actif. 
                      Vous devez <strong>terminer le repêchage, sauvegarder les paramètres</strong>, puis relancer un nouveau draft pour modifier ces paramètres.
                    </div>
                  )}
                  <div className="mb-3">
                    <label htmlFor="draftTimePerPick" className="form-label">
                      Temps alloué par sélection (en minutes)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="draftTimePerPick"
                      value={draftTimePerPick || ''}
                      disabled={dynastyOnlyDisabled || currentDraftModeInDB}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setDraftTimePerPick(0);
                        } else if (/^\d+$/.test(value)) {
                          const numValue = parseInt(value, 10);
                          setDraftTimePerPick(numValue);
                        }
                      }}
                    />
                    <small className="text-muted">
                      Nombre de minutes par sélection (recommandé: entre 1 et 60 minutes)
                    </small>
                  </div>
                  
                  <div className="mb-4">
                    <h6 className="mb-3">Format du repêchage</h6>
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="draftFormatType"
                        id="linearFormat"
                        value="linear"
                        checked={draftFormatType === 'linear'}
                        disabled={dynastyOnlyDisabled || currentDraftModeInDB}
                        onChange={() => setDraftFormatType('linear')}
                      />
                      <label className="form-check-label" htmlFor="linearFormat">
                        Linéaire (même ordre à chaque tour)
                      </label>
                      <div className="form-text ms-4 draft-format-helper">
                        Tour 1: Équipe A → B → C<br />
                        Tour 2: Équipe A → B → C
                      </div>
                    </div>
                    
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="draftFormatType"
                        id="serpentineFormat"
                        value="serpentine"
                        checked={draftFormatType === 'serpentine'}
                        disabled={dynastyOnlyDisabled || currentDraftModeInDB}
                        onChange={() => setDraftFormatType('serpentine')}
                      />
                      <label className="form-check-label" htmlFor="serpentineFormat">
                        Serpentin (ordre inverse aux tours pairs)
                      </label>
                      <div className="form-text ms-4 draft-format-helper">
                        Tour 1: Équipe A → B → C<br />
                        Tour 2: Équipe C → B → A
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <h6 className="mb-3">Ordre du repêchage</h6>
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="draftOrderType"
                        id="randomOrder"
                        value="random"
                        checked={draftOrderType === 'random'}
                        disabled={dynastyOnlyDisabled || currentDraftModeInDB}
                        onChange={() => setDraftOrderType('random')}
                      />
                      <label className="form-check-label" htmlFor="randomOrder">
                        Générer un ordre aléatoire
                      </label>
                    </div>
                    
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="draftOrderType"
                        id="manualOrder"
                        value="manual"
                        checked={draftOrderType === 'manual'}
                        disabled={dynastyOnlyDisabled || currentDraftModeInDB}
                        onChange={() => setDraftOrderType('manual')}
                      />
                      <label className="form-check-label" htmlFor="manualOrder">
                        Définir l'ordre manuellement
                      </label>
                    </div>

                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="draftOrderType"
                        id="standingsOrder"
                        value="standings"
                        checked={draftOrderType === 'standings'}
                        disabled={dynastyOnlyDisabled || currentDraftModeInDB}
                        onChange={() => setDraftOrderType('standings')}
                      />
                      <label className="form-check-label" htmlFor="standingsOrder">
                        Utiliser l'ordre du classement actuel
                      </label>
                      <div className="form-text ms-4">
                        L'équipe avec le moins de points débute le repêchage, celle avec le plus de points termine.
                      </div>
                    </div>
                    
                    {draftOrderType === 'random' && (
                      <div>
                        <button 
                          className="btn btn-outline-primary d-flex align-items-center mb-3"
                          onClick={generateRandomDraftOrder}
                          disabled={dynastyOnlyDisabled || currentDraftModeInDB}
                        >
                          <Shuffle size={18} className="me-2" />
                          Générer un ordre aléatoire
                        </button>
                        
                        {manualDraftOrder.length > 0 && (
                          <div>
                            <p className="text-muted mb-2">
                              Ordre de repêchage actuel:
                            </p>
                            <div className="list-group">
                              {manualDraftOrder.map((userId, index) => {
                                const member = leagueMembers.find(m => m.users_user_id === userId);
                                if (!member) return null;
                                return (
                                  <div
                                    key={userId}
                                    className="list-group-item d-flex align-items-center"
                                  >
                                    <span className="badge bg-primary me-3">{index + 1}</span>
                                    <span>{member.username}</span>
                                    {index === 0 && (
                                      <span className="ms-2 badge bg-success">Tour initial</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {draftOrderType === 'manual' && (
                      <div>
                        <p className="text-muted mb-2">
                          {currentDraftModeInDB ? 'Ordre du repêchage (verrouillé pendant le draft)' : 'Glissez-déposez les équipes pour définir l\'ordre du repêchage'}
                        </p>
                        <div className="list-group">
                          {manualDraftOrder.map((userId, index) => {
                            const member = leagueMembers.find(m => m.users_user_id === userId);
                            if (!member) return null;
                            return (
                              <div
                                key={userId}
                                className={`list-group-item d-flex align-items-center ${currentDraftModeInDB ? 'bg-light' : ''}`}
                                draggable={!currentDraftModeInDB}
                                onDragStart={!currentDraftModeInDB ? (e) => handleDragStart(e, userId) : undefined}
                                onDragOver={!currentDraftModeInDB ? handleDragOver : undefined}
                                onDrop={!currentDraftModeInDB ? (e) => handleDrop(e, userId) : undefined}
                                style={{ cursor: currentDraftModeInDB ? 'not-allowed' : 'move' }}
                              >
                                <span className="badge bg-primary me-3">{index + 1}</span>
                                <span>{member.username}</span>
                                {index === 0 && (
                                  <span className="ms-2 badge bg-success">Tour initial</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {draftOrderType === 'standings' && (
                      <div>
                        <button
                          className="btn btn-outline-primary d-flex align-items-center mb-3"
                          onClick={generateStandingsDraftOrder}
                          disabled={dynastyOnlyDisabled || currentDraftModeInDB || isLoadingStandingsOrder}
                        >
                          {isLoadingStandingsOrder ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          ) : (
                            <ListOrdered size={18} className="me-2" />
                          )}
                          Charger l'ordre selon le classement
                        </button>

                        {manualDraftOrder.length > 0 && (
                          <div>
                            <p className="text-muted mb-2">
                              Ordre de repêchage actuel:
                            </p>
                            <div className="list-group">
                              {manualDraftOrder.map((userId, index) => {
                                const member = leagueMembers.find(m => m.users_user_id === userId);
                                if (!member) return null;
                                return (
                                  <div
                                    key={userId}
                                    className="list-group-item d-flex align-items-center"
                                  >
                                    <span className="badge bg-primary me-3">{index + 1}</span>
                                    <span>{member.username}</span>
                                    {index === 0 && (
                                      <span className="ms-2 badge bg-success">Tour initial</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Générer l'ordre complet selon le format */}
                    {draftFormatType === 'serpentine' && (
                      <div className="card mt-4">
                        <div className="card-header">Générer l’ordre complet (serpentin)</div>
                        <div className="card-body">
                          <div className="row g-2 align-items-end">
                            <div className="col-12 col-sm-6 col-md-auto">
                              <div className="form-group">
                                <label className="form-label">Nombre de rondes</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  id="nbRounds"
                                  value={nbRounds ?? ''}
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  disabled={currentDraftModeInDB}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '') {
                                      setNbRounds(0);
                                    } else if (/^\d+$/.test(value)) {
                                      const numValue = parseInt(value, 10);
                                      setNbRounds(numValue);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value === '' || nbRounds === 0) {
                                      setNbRounds(0);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                            <div className="col-12 col-sm-6">
                              <button
                                type="button"
                                className={`btn ${hasSerpentineOrderGenerated ? 'btn-success' : 'btn-primary'} w-100 text-wrap`}
                                style={{ display: '-webkit-box', WebkitLineClamp: 2 as any, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }}
                                onClick={handleGenerateSerpentine}
                                disabled={dynastyOnlyDisabled || currentDraftModeInDB}
                                title={currentDraftModeInDB ? 'Désactiver le mode repêchage d’abord' : ''}
                              >
                                Générer l’ordre serpentin
                              </button>
                            </div>
                          </div>
                        </div>
                        {!hasSerpentineOrderGenerated && (
                          <div className="alert alert-warning mt-3 mb-0">
                            <strong>⚠️ Important:</strong> Vous devez générer l'ordre serpentin avant de pouvoir lancer le draft.
                          </div>
                        )}
                      </div>
                    )}

                    {draftFormatType === 'linear' && (
                      <div className="card mt-4">
                        <div className="card-header">Générer l’ordre complet (linéaire)</div>
                        <div className="card-body">
                          <div className="row g-2 align-items-end">
                            <div className="col-12 col-sm-6 col-md-auto">
                              <div className="form-group">
                                <label className="form-label">Nombre de rondes</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  id="nbRounds"
                                  value={nbRounds ?? ''}
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  disabled={currentDraftModeInDB}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '') {
                                      setNbRounds(0);
                                    } else if (/^\d+$/.test(value)) {
                                      const numValue = parseInt(value, 10);
                                      setNbRounds(numValue);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value === '' || nbRounds === 0) {
                                      setNbRounds(0);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                            <div className="col-12 col-sm-6">
                              <button
                                type="button"
                                className={`btn ${hasLinearOrderGenerated ? 'btn-success' : 'btn-primary'} w-100 text-wrap`}
                                style={{ display: '-webkit-box', WebkitLineClamp: 2 as any, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }}
                                onClick={handleGenerateLinear}
                                disabled={dynastyOnlyDisabled || currentDraftModeInDB}
                                title={currentDraftModeInDB ? 'Désactiver le mode repêchage d’abord' : ''}
                              >
                                Générer l’ordre linéaire
                              </button>
                            </div>
                          </div>
                        </div>
                        {!hasLinearOrderGenerated && (
                          <div className="alert alert-warning mt-3 mb-0">
                            <strong>⚠️ Important:</strong> Vous devez générer l'ordre linéaire avant de pouvoir lancer le draft.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex align-items-center">
              <DollarSign size={20} className="me-2" />
              <h5 className="mb-0">{translate('salary.cap.settings', language)}</h5>
            </div>
            <div className="card-body">
              <div className="mb-4">
                <h6 className="mb-3">{translate('main.club.salary.cap', language)}</h6>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="salaryCap"
                    checked={isSalaryCap}
                    onChange={() => setIsSalaryCap(!isSalaryCap)}
                  />
                  <label className="form-check-label" htmlFor="salaryCap">
                    {translate('enable.salary.cap', language)}
                  </label>
                </div>

                {isSalaryCap && (
                  <div className="mb-3">
                    <label htmlFor="salaryCapAmount" className="form-label">
                      {translate('salary.cap.amount', language)}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <DollarSign size={16} />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="salaryCapAmount"
                        value={salaryCapAmount || ''}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setSalaryCapAmount(0);
                          } else if (/^\d+$/.test(value)) {
                            const numValue = parseInt(value, 10);
                            setSalaryCapAmount(numValue);
                          }
                        }}
                      />
                    </div>
                    <small className="text-muted">
                      {translate('default.amount', language)}: {formatCurrency(88000000)} - Valeur actuelle: {formatCurrency(salaryCapAmount)}
                    </small>
                  </div>
                )}
              </div>

              <div>
                <h6 className="mb-3">{translate('reserve.team.salary.cap', language)}</h6>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="reserveSalaryCap"
                    checked={isReserveSalaryCap}
                    onChange={() => setIsReserveSalaryCap(!isReserveSalaryCap)}
                  />
                  <label className="form-check-label" htmlFor="reserveSalaryCap">
                    {translate('enable.reserve.salary.cap', language)}
                  </label>
                </div>

                {isReserveSalaryCap && (
                  <div className="mb-3">
                    <label htmlFor="reserveSalaryCapAmount" className="form-label">
                      {translate('reserve.cap.amount', language)}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <DollarSign size={16} />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="reserveSalaryCapAmount"
                        value={reserveSalaryCapAmount || ''}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setReserveSalaryCapAmount(0);
                          } else if (/^\d+$/.test(value)) {
                            const numValue = parseInt(value, 10);
                            setReserveSalaryCapAmount(numValue);
                          }
                        }}
                      />
                    </div>
                    <small className="text-muted">
                      {translate('default.amount', language)}: {formatCurrency(15000000)} - Valeur actuelle: {formatCurrency(reserveSalaryCapAmount)}
                    </small>
                  </div>
                )}
              </div>

            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex align-items-center">
              <Shuffle size={20} className="me-2" />
              <h5 className="mb-0">Paramètres des transactions</h5>
            </div>
            <div className="card-body">
              {isStandardFlexible && (
                <div className="alert alert-secondary d-flex align-items-center gap-2 mb-3 py-2">
                  <span>⚙️</span>
                  <span className="small">Les choix de repêchage échangeables sont disponibles uniquement en mode Dynasty/Keeper.</span>
                </div>
              )}
              <div className="mb-3">
                <label htmlFor="numberOfPicks" className="form-label">
                  Nombre de choix de repêchage échangeables par équipe
                </label>
                <div className="d-flex gap-2 align-items-end">
                  <div className="flex-grow-1">
                    <input
                      type="text"
                      className="form-control"
                      id="numberOfPicks"
                      value={numberOfPicks || ''}
                      disabled={dynastyOnlyDisabled}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setNumberOfPicks(0);
                        } else if (/^\d+$/.test(value)) {
                          const numValue = parseInt(value, 10);
                          setNumberOfPicks(numValue);
                        }
                      }}
                    />
                  </div>
                  {isCommissioner && !isStandardFlexible && numberOfPicks > 0 && (
                    <button
                      type="button"
                      className="btn btn-warning btn-sm"
                      onClick={() => setShowGenerateDraftPicksModal(true)}
                      disabled={isGeneratingDraftPicks}
                    >
                      {isGeneratingDraftPicks ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Génération...
                        </>
                      ) : (
                        'Générer les choix'
                      )}
                    </button>
                  )}
                </div>
                <small className="text-muted">
                  Détermine combien de choix de repêchage chaque équipe peut utiliser dans les échanges (exemple: 7 = les équipes peuvent échanger leurs choix de 1ère à 7ème ronde)
                </small>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex align-items-center">
              <Users size={20} className="me-2" />
              <h5 className="mb-0">{translate('team.composition', language)}</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label htmlFor="forwards" className="form-label">
                    {translate('number.forwards', language)}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="forwards"
                    value={teamComposition.forwards ?? ''}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setTeamComposition({
                          ...teamComposition,
                          forwards: 0
                        });
                      } else if (/^\d+$/.test(value)) {
                        const numValue = parseInt(value, 10);
                        setTeamComposition({
                          ...teamComposition,
                          forwards: numValue
                        });
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || teamComposition.forwards === 0) {
                        setTeamComposition({
                          ...teamComposition,
                          forwards: 0
                        });
                      }
                    }}
                  />
                </div>
                <div className="col-md-3">
                  <label htmlFor="defensemen" className="form-label">
                    {translate('number.defensemen', language)}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="defensemen"
                    value={teamComposition.defensemen ?? ''}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setTeamComposition({
                          ...teamComposition,
                          defensemen: 0
                        });
                      } else if (/^\d+$/.test(value)) {
                        const numValue = parseInt(value, 10);
                        setTeamComposition({
                          ...teamComposition,
                          defensemen: numValue
                        });
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || teamComposition.defensemen === 0) {
                        setTeamComposition({
                          ...teamComposition,
                          defensemen: 0
                        });
                      }
                    }}
                  />
                </div>
                <div className="col-md-3">
                  <label htmlFor="goalies" className="form-label">
                    {translate('number.goalies', language)}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="goalies"
                    value={teamComposition.goalies ?? ''}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setTeamComposition({
                          ...teamComposition,
                          goalies: 0
                        });
                      } else if (/^\d+$/.test(value)) {
                        const numValue = parseInt(value, 10);
                        setTeamComposition({
                          ...teamComposition,
                          goalies: numValue
                        });
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || teamComposition.goalies === 0) {
                        setTeamComposition({
                          ...teamComposition,
                          goalies: 0
                        });
                      }
                    }}
                  />
                </div>
                <div className="col-md-3">
                  <label htmlFor="reserves" className="form-label">
                    {translate('number.reserves', language)}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="reserves"
                    value={teamComposition.reserves ?? ''}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setTeamComposition({
                          ...teamComposition,
                          reserves: 0
                        });
                      } else if (/^\d+$/.test(value)) {
                        const numValue = parseInt(value, 10);
                        setTeamComposition({
                          ...teamComposition,
                          reserves: numValue
                        });
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || teamComposition.reserves === 0) {
                        setTeamComposition({
                          ...teamComposition,
                          reserves: 0
                        });
                      }
                    }}
                  />
                </div>
                <div className="col-md-3">
                  <label htmlFor="scratches" className="form-label">
                    Nombre de Scratch Players
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="scratches"
                    value={teamComposition.scratches ?? ''}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setTeamComposition({
                          ...teamComposition,
                          scratches: 0
                        });
                      } else if (/^\d+$/.test(value)) {
                        const numValue = parseInt(value, 10);
                        setTeamComposition({
                          ...teamComposition,
                          scratches: numValue
                        });
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || teamComposition.scratches === 0) {
                        setTeamComposition({
                          ...teamComposition,
                          scratches: 0
                        });
                      }
                    }}
                  />
                </div>

                <div className="mt-4 border-top pt-4">
                  <div className="alert alert-info">
                    <h6 className="mb-2">📋 Légende des types de joueurs</h6>
                    <ul className="mb-0">
                      <li><strong>Joueurs de réserve :</strong> Ne comptent pas sur le plafond salarial de l'équipe principale (mais comptent sur celui de réserve) et leurs points ne sont pas comptés dans le classement.</li>
                      <li><strong>Scratch players :</strong> Comptent sur la masse salariale principale mais leurs points ne comptent pas dans le classement.</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-4 border-top pt-4">
                  <div className="d-flex align-items-center mb-3">
                    <Bandage size={18} className="text-primary me-2" />
                    <h6 className="mb-0">Liste de Blessés</h6>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <label htmlFor="injuredReserve" className="form-label">
                        Nombre maximum de joueurs sur la liste des blessés
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="injuredReserve"
                        value={injuredReserveLimit ?? ''}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setInjuredReserveLimit(0);
                          } else if (/^\d+$/.test(value)) {
                            const numValue = parseInt(value, 10);
                            setInjuredReserveLimit(numValue);
                          }
                        }}
                      />
                      <small className="text-muted">
                        Les joueurs sur la liste des blessés ne comptent pas dans la limite de l'effectif ainsi que sur la masse salarial
                      </small>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="daysToComeback" className="form-label">
                        Délai maximum de retour (en jours)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="daysToComeback"
                        value={daysToComeback ?? ''}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setDaysToComeback(0);
                          } else if (/^\d+$/.test(value)) {
                            const numValue = parseInt(value, 10);
                            setDaysToComeback(numValue);
                          }
                        }}
                      />
                      <small className="text-muted">
                        Nombre de jours maximum pour réactiver un joueur blessé avant qu'il ne devienne agent libre
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex align-items-center">
              <ArrowDownCircle size={20} className="me-2" />
              <h5 className="mb-0">{translate('season.limits', language)}</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <label htmlFor="maxDemotions" className="form-label">
                    {translate('max.demotions.per.season', language)}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="maxDemotions"
                    value={maxDemotions ?? ''}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setMaxDemotions(0);
                      } else if (/^\d+$/.test(value)) {
                        const numValue = parseInt(value, 10);
                        setMaxDemotions(numValue);
                      }
                    }}
                  />
                  <small className="text-muted">
                    {translate('demotion.settings.description', language)}
                  </small>
                </div>
                <div className="col-md-6">
                  <label htmlFor="maxSignings" className="form-label">
                    {translate('max.signings.per.season', language)}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="maxSignings"
                    value={maxSignings ?? ''}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setMaxSignings(0);
                      } else if (/^\d+$/.test(value)) {
                        const numValue = parseInt(value, 10);
                        setMaxSignings(numValue);
                      }
                    }}
                  />
                  <small className="text-muted">
                    {translate('signing.settings.description', language)}
                  </small>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <label className="form-label">
                    {translate('count.fa.signings.label', language)}
                  </label>
                  <div className="d-flex align-items-center gap-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="countFreeAgentSignings"
                        id="countFreeAgentSignings_yes"
                        checked={countFreeAgentSignings === true}
                        disabled={!isCommissioner}
                        onChange={() => setCountFreeAgentSignings(true)}
                      />
                      <label className="form-check-label" htmlFor="countFreeAgentSignings_yes">
                        {language === 'fr' ? 'Oui' : 'Yes'}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="countFreeAgentSignings"
                        id="countFreeAgentSignings_no"
                        checked={countFreeAgentSignings === false}
                        disabled={!isCommissioner}
                        onChange={() => setCountFreeAgentSignings(false)}
                      />
                      <label className="form-check-label" htmlFor="countFreeAgentSignings_no">
                        {language === 'fr' ? 'Non' : 'No'}
                      </label>
                    </div>
                  </div>
                  <small className="text-muted d-block mt-1">
                    {translate('count.fa.signings.description', language)}
                  </small>
                </div>
              </div>
              <div className="mt-4 border-top pt-4">
                <div className="d-flex align-items-center mb-3">
                  <Calendar size={18} className="text-primary me-2" />
                  <h6 className="mb-0">
                    {language === 'fr' ? 'Date limite pour les promotions et rétrogradations' : 'Promotion/demotion deadline'}
                  </h6>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <label htmlFor="promotionDeadline" className="form-label">
                      {language === 'fr' ? 'Date limite' : 'Deadline date'}
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="promotionDeadline"
                      value={promotionDeadline}
                      disabled={!isCommissioner}
                      onChange={(e) => setPromotionDeadline(e.target.value)}
                    />
                    <small className="text-muted d-block mt-1">
                      {language === 'fr'
                        ? 'A partir de cette date, les DG ne pourront plus promouvoir ni rétrograder des joueurs. Laissez vide pour ne pas définir de limite.'
                        : 'From this date, GMs will no longer be able to promote or demote players. Leave empty for no limit.'}
                    </small>
                    {promotionDeadline && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary mt-2"
                        disabled={!isCommissioner}
                        onClick={() => setPromotionDeadline('')}
                      >
                        {language === 'fr' ? 'Effacer la date limite' : 'Clear deadline'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 border-top pt-4">
                <div className="d-flex align-items-center mb-3">
                  <Calendar size={18} className="text-primary me-2" />
                  <h6 className="mb-0">Date limite des échanges</h6>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <label htmlFor="tradeDeadline" className="form-label">
                      Date limite pour les échanges et signatures d'agents libres
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="tradeDeadline"
                      value={tradeDeadline}
                      disabled={!isCommissioner}
                      onChange={(e) => setTradeDeadline(e.target.value)}
                    />
                    <small className="text-muted d-block mt-1">
                      Après cette date, il sera impossible de faire des échanges ou de signer des agents libres. Laissez vide pour aucune restriction.
                    </small>
                    {tradeDeadline && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary mt-2"
                        disabled={!isCommissioner}
                        onClick={() => setTradeDeadline('')}
                      >
                        {language === 'fr' ? 'Effacer la date limite' : 'Clear deadline'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 border-top pt-4">
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="noMovementToggle"
                    checked={isNoMovementWindowEnabled}
                    disabled={!isCommissioner}
                    onChange={() => setIsNoMovementWindowEnabled(!isNoMovementWindowEnabled)}
                  />
                  <label className="form-check-label" htmlFor="noMovementToggle">
                    {translate('season.no.movement.toggle', language) || "Bloquer les modifications d'alignement durant une plage horaire"}
                  </label>
                  <small className="text-muted d-block mt-1">
                    {translate('season.no.movement.description', language) || 'Empêche les DG de modifier leur alignement entre les heures définies.'}
                  </small>
                </div>
                {isNoMovementWindowEnabled && (
                  <>
                    <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="noMovementStartTime" className="form-label">
                        {language === 'fr' ? 'Heure de début du blocage (HH:MM)' : 'Blocking start time (HH:MM)'}
                      </label>
                      <input
                        type="time"
                        className="form-control"
                        id="noMovementStartTime"
                        value={noMovementStartTime}
                        disabled={!isCommissioner}
                        onChange={(e) => setNoMovementStartTime(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="noMovementEndTime" className="form-label">
                        {language === 'fr' ? 'Heure de fin du blocage (HH:MM)' : 'Blocking end time (HH:MM)'}
                      </label>
                      <input
                        type="time"
                        className="form-control"
                        id="noMovementEndTime"
                        value={noMovementEndTime}
                        disabled={!isCommissioner}
                        onChange={(e) => setNoMovementEndTime(e.target.value)}
                      />
                    </div>
                    </div>
                    <div className="row g-3 mt-1">
                      <div className="col-md-6">
                        <label htmlFor="noMovementStartDay" className="form-label">
                          {language === 'fr' ? 'Jour de début du blocage' : 'Blocking start day'}
                        </label>
                        <select
                          className="form-select"
                          id="noMovementStartDay"
                          value={noMovementStartDay}
                          disabled={!isCommissioner}
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value, 10);
                            if (!Number.isNaN(parsed)) {
                              setNoMovementStartDay(parsed);
                            }
                          }}
                        >
                          {dayOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="noMovementEndDay" className="form-label">
                          {language === 'fr' ? 'Jour de fin du blocage' : 'Blocking end day'}
                        </label>
                        <select
                          className="form-select"
                          id="noMovementEndDay"
                          value={noMovementEndDay}
                          disabled={!isCommissioner}
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value, 10);
                            if (!Number.isNaN(parsed)) {
                              setNoMovementEndDay(parsed);
                            }
                          }}
                        >
                          {dayOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}

                <button
              className="btn btn-danger mt-4"
              onClick={handleResetMovementsAndSignings}
            >
              Réinitialiser les mouvements et signatures
            </button>

              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex align-items-center">
              <Shield size={20} className="me-2" />
              <h5 className="mb-0">{translate('protection.list.settings', language)}</h5>
            </div>
            <div className="card-body">
              {isStandardFlexible && (
                <div className="alert alert-secondary d-flex align-items-center gap-2 mb-3 py-2">
                  <span>⚙️</span>
                  <span className="small">La liste de protection est disponible uniquement en mode Dynasty/Keeper. Elle permet aux équipes de protéger leurs joueurs entre les saisons.</span>
                </div>
              )}
              <div className="form-check form-switch mb-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="useProtectionList"
                  checked={useProtectionList}
                  disabled={dynastyOnlyDisabled}
                  onChange={() => setUseProtectionList(!useProtectionList)}
                />
                <label className="form-check-label" htmlFor="useProtectionList">
                  Activer la liste de protection
                </label>
                <small className="text-muted d-block mt-1">
                  Si activé, les équipes devront soumettre une liste de joueurs protégés en fin de saison
                </small>
              </div>

              {useProtectionList && (
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label htmlFor="maxDateProtection" className="form-label d-flex align-items-center">
                      <Calendar size={18} className="text-primary me-2" />
                      Date limite pour soumettre la liste
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="maxDateProtection"
                      value={maxDateProtectionList}
                      disabled={dynastyOnlyDisabled}
                      onChange={(e) => setMaxDateProtectionList(e.target.value)}
                      required={useProtectionList}
                    />
                    <small className="text-muted">
                      Date après laquelle les listes ne pourront plus être modifiées. Les joueurs non protégés seront alors libérés et deviendront agents libres
                    </small>
                  </div>
                </div>
              )}

              {useProtectionList && (
                <div className="row g-3">
                  <div className="col-md-4">
                    <label htmlFor="forwardsProtect" className="form-label">
                      {translate('number.forwards.protect', language)}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="forwardsProtect"
                      value={protectionList.forwards ?? ''}
                      disabled={dynastyOnlyDisabled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setProtectionList({
                            ...protectionList,
                            forwards: 0
                          });
                        } else if (/^\d+$/.test(value)) {
                          const numValue = parseInt(value, 10);
                          setProtectionList({
                            ...protectionList,
                            forwards: numValue
                          });
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="defensemenProtect" className="form-label">
                      {translate('number.defensemen.protect', language)}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="defensemenProtect"
                      value={protectionList.defensemen ?? ''}
                      disabled={dynastyOnlyDisabled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setProtectionList({
                            ...protectionList,
                            defensemen: 0
                          });
                        } else if (/^\d+$/.test(value)) {
                          const numValue = parseInt(value, 10);
                          setProtectionList({
                            ...protectionList,
                            defensemen: numValue
                          });
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="goaliesProtect" className="form-label">
                      {translate('number.goalies.protect', language)}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="goaliesProtect"
                      value={protectionList.goalies ?? ''}
                      disabled={dynastyOnlyDisabled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setProtectionList({
                            ...protectionList,
                            goalies: 0
                          });
                        } else if (/^\d+$/.test(value)) {
                          const numValue = parseInt(value, 10);
                          setProtectionList({
                            ...protectionList,
                            goalies: numValue
                          });
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="reservesProtect" className="form-label">
                      Nombre de joueur de Réserve à Protéger
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="reservesProtect"
                      value={protectionList.reserves ?? ''}
                      disabled={dynastyOnlyDisabled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setProtectionList({
                            ...protectionList,
                            reserves: 0
                          });
                        } else if (/^\d+$/.test(value)) {
                          const numValue = parseInt(value, 10);
                          setProtectionList({
                            ...protectionList,
                            reserves: numValue
                          });
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex align-items-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                <circle cx="12" cy="12" r="10"/>
                <polygon points="10,8 16,12 10,16 10,8"/>
              </svg>
              <h5 className="mb-0">Système de Points</h5>
            </div>
            <div className="card-body">
              <div className="row g-4">
                <div className="col-12">
                  <h6 className="mb-3 text-primary">⚡ Attaquants</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="forwardGoal" className="form-label">
                        Points pour un but
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="forwardGoal"
                        value={pointsSettings.forwardGoal ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setPointsSettings({
                              ...pointsSettings,
                              forwardGoal: 0
                            });
                          } else if (/^\d*\.?\d*$/.test(value)) {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              setPointsSettings({
                                ...pointsSettings,
                                forwardGoal: numValue
                              });
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="forwardAssist" className="form-label">
                        Points pour une passe
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="forwardAssist"
                        value={pointsSettings.forwardAssist ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setPointsSettings({
                              ...pointsSettings,
                              forwardAssist: 0
                            });
                          } else if (/^\d*\.?\d*$/.test(value)) {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              setPointsSettings({
                                ...pointsSettings,
                                forwardAssist: numValue
                              });
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <h6 className="mb-3 text-primary">🛡️ Défenseurs</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="defenseGoal" className="form-label">
                        Points pour un but
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="defenseGoal"
                        value={pointsSettings.defenseGoal ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setPointsSettings({
                              ...pointsSettings,
                              defenseGoal: 0
                            });
                          } else if (/^\d*\.?\d*$/.test(value)) {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              setPointsSettings({
                                ...pointsSettings,
                                defenseGoal: numValue
                              });
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="defenseAssist" className="form-label">
                        Points pour une passe
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="defenseAssist"
                        value={pointsSettings.defenseAssist ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setPointsSettings({
                              ...pointsSettings,
                              defenseAssist: 0
                            });
                          } else if (/^\d*\.?\d*$/.test(value)) {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              setPointsSettings({
                                ...pointsSettings,
                                defenseAssist: numValue
                              });
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <h6 className="mb-3 text-primary">🥅 Gardiens</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label htmlFor="goalieGoal" className="form-label">
                        Points pour un but
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="goalieGoal"
                        value={pointsSettings.goalieGoal ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setPointsSettings({
                              ...pointsSettings,
                              goalieGoal: 0
                            });
                          } else if (/^\d*\.?\d*$/.test(value)) {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              setPointsSettings({
                                ...pointsSettings,
                                goalieGoal: numValue
                              });
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="goalieAssist" className="form-label">
                        Points pour une passe
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="goalieAssist"
                        value={pointsSettings.goalieAssist ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setPointsSettings({
                              ...pointsSettings,
                              goalieAssist: 0
                            });
                          } else if (/^\d*\.?\d*$/.test(value)) {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              setPointsSettings({
                                ...pointsSettings,
                                goalieAssist: numValue
                              });
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="goalieWin" className="form-label">
                        Points pour une victoire
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="goalieWin"
                        value={pointsSettings.goalieWin ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setPointsSettings({
                              ...pointsSettings,
                              goalieWin: 0
                            });
                          } else if (/^\d*\.?\d*$/.test(value)) {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              setPointsSettings({
                                ...pointsSettings,
                                goalieWin: numValue
                              });
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="goalieShutout" className="form-label">
                        Points pour un blanchissage
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="goalieShutout"
                        value={pointsSettings.goalieShutout ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setPointsSettings({
                              ...pointsSettings,
                              goalieShutout: 0
                            });
                          } else if (/^\d*\.?\d*$/.test(value)) {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              setPointsSettings({
                                ...pointsSettings,
                                goalieShutout: numValue
                              });
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="row g-3 mt-2">
                    <div className="col-md-4">
                      <label htmlFor="goalieOtLoss" className="form-label">
                        Points pour une défaite en prolongation
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="goalieOtLoss"
                        value={pointsSettings.goalieOtLoss ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setPointsSettings({
                              ...pointsSettings,
                              goalieOtLoss: 0
                            });
                          } else if (/^\d*\.?\d*$/.test(value)) {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              setPointsSettings({
                                ...pointsSettings,
                                goalieOtLoss: numValue
                              });
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isCommissioner && (
            <div className="d-grid gap-2 mt-4">
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={saving}
              >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {translate('saving.changes', language)}
                </>
              ) : (
                translate('save.changes', language)
              )}
            </button>
            {/* <button
              className="btn btn-danger mt-4"
              onClick={handleResetMovementsAndSignings}
            >
              Réinitialiser les mouvements et signatures
            </button> */}
            </div>
          )}
        </div>
      </div>
      
      {/* Modal de génération des choix de repêchage */}
      <GenerateDraftPicksModal
        show={showGenerateDraftPicksModal}
        onHide={() => setShowGenerateDraftPicksModal(false)}
        onConfirm={handleGenerateDraftPicks}
        numberOfRounds={numberOfPicks}
        loading={isGeneratingDraftPicks}
      />
    </div>
  );
}